/**
 * ThinkingGroup — rendu de TOUS les blocs de réflexion d'un échange, hoistés
 * en une seule liste repliée affichée en tête (design 2026-06-16, voir
 * docs/features/agent-case-discovery-v3/). Collapsed par défaut, expandable :
 * dépliée, elle liste chaque bloc individuellement avec sa durée.
 *
 * Pourquoi une liste unique au lieu de bulles inline : quand l'agent enchaîne
 * réflexion → outils → réflexion → outils, les bulles inline fragmentaient la
 * timeline et noyaient l'action. La réflexion est du matériau de référence, pas
 * le récit de l'action — on la sort du flux chronologique.
 *
 * Mirror component: addin-word hector-agent-edition-v3 ThinkingGroup.tsx — keep
 * behavior in sync (palette slate côté add-in).
 */

import React, { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { TextShimmer } from './TextShimmer';
import { ReasoningLine, TimelineNode } from './ToolGroup';
import type { ThinkingTimelineItem } from '../../utils/groupTimelineItems';

interface ThinkingGroupProps {
  /** Tous les blocs de réflexion de l'échange, dans l'ordre d'émission. */
  thinkings: ThinkingTimelineItem[];
}

const ThinkingGroupImpl: React.FC<ThinkingGroupProps> = ({ thinkings }) => {
  // Collapsed par défaut, toggle manuel uniquement — même décision que le
  // reasoning inline d'origine (c1cda9854) et que ToolGroup : aucun useEffect
  // d'auto-expand, le passage live → terminé ne replie jamais une vue ouverte.
  const [isExpanded, setIsExpanded] = useState(false);

  // Un bloc « actif » démarre vide (premier delta pas encore arrivé) — on ne le
  // compte pas tant qu'il n'a pas de contenu, pour ne pas afficher une liste
  // fantôme. La TypingIndicator couvre ce micro-instant (comportement hérité).
  const visible = thinkings.filter(t => t.thinking.content.length > 0);
  if (visible.length === 0) return null;

  const anyActive = visible.some(t => t.thinking.isActive);
  const count = visible.length;
  // Le nombre n'intéresse pas — juste le pluriel.
  const label = count > 1 ? 'Réflexions' : 'Réflexion';

  const totalMs = visible.reduce((sum, t) => sum + (t.thinking.durationMs ?? 0), 0);
  const totalSec = totalMs > 0 ? Math.round(totalMs / 1000) : null;

  return (
    // pt-1 (et non py-1) : boîte flush en bas, comme ToolGroup et le texte — la
    // distance jusqu'à la plume reste constante quel que soit le dernier item.
    <div className="pt-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-[13px] text-grey-600 hover:text-grey-600 transition-colors"
      >
        {anyActive ? (
          <TextShimmer className="font-normal">{label}</TextShimmer>
        ) : (
          <span className="font-normal">{label}</span>
        )}
        {totalSec !== null && !anyActive && (
          <span className="text-grey-400">· {totalSec}s</span>
        )}
        <ChevronUp
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? '' : 'rotate-180'}`}
        />
      </button>
      {isExpanded && (
        <div className="ml-1 mt-2">
          {/* Mêmes nœuds que la timeline d'un ToolGroup : cerveaux reliés par le
              trait vertical, chaque réflexion cropée + « Voir plus ». */}
          {visible.map((t, i) => (
            <TimelineNode key={t.id} showConnector={i < visible.length - 1}>
              <ReasoningLine item={t} />
            </TimelineNode>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Mémoïsé avec comparateur custom : groupTimelineItems recrée le tableau
 * `thinkings` à chaque delta (nouvelle référence), mais les items gardent leur
 * référence tant que le reducer ne les mute pas. Comparer item par item évite
 * de re-render à chaque token de contenu d'un autre item de la timeline.
 */
export const ThinkingGroup = React.memo(
  ThinkingGroupImpl,
  (prev, next) =>
    prev.thinkings.length === next.thinkings.length &&
    prev.thinkings.every((t, i) => t === next.thinkings[i]),
);
